import React, { useContext, useState, useEffect } from 'react';
import { Button, Card, Form, Input, Select, DatePicker, TimePicker, Row, Col, message, Spin, Modal } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { addOvertime, getAllTasksForCardView } from '../../utils/API';
import { AuthContext } from "../../Auth/AuthContext";

const { Option } = Select;

interface Task {
  _id: string;
  taskName: string;
  taskDesc: string;
  status: string;
}
interface OvertimeFormModalProps {
  visible: boolean;
  onClose: () => void;
  onTaskCreated: () => void;
}
const Addovertimemodal: React.FC<OvertimeFormModalProps> = ({ visible, onClose, onTaskCreated }) => {
  const [form] = Form.useForm();
  const { authData } = useContext(AuthContext);
  const userId = authData.user._id;
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalHours, setTotalHours] = useState<string>('0.00');
  const [formattedHours, setFormattedHours] = useState<string>('0h 00m');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskLoading, setTaskLoading] = useState<boolean>(false);

  // Fetch tasks on component mount

  const fetchTasks = async () => {
    try {
      setTaskLoading(true);
      const response = await getAllTasksForCardView("", "");
      if (response?.data) {
        setTasks(response.data);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setTaskLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const calculateHours = (start: Moment | null, end: Moment | null) => {
    if (!start || !end) {
      setTotalHours('0.00');
      setFormattedHours('0h 00m');
      return;
    }

    if (end.isBefore(start)) {
      message.error('End time must be after start time');
      setTotalHours('0.00');
      setFormattedHours('0h 00m');
      return;
    }

    const duration = moment.duration(end.diff(start));
    const totalHoursValue = duration.asHours();
    const hours = Math.floor(totalHoursValue);
    const minutes = Math.round((totalHoursValue - hours) * 60);

    const adjustedHours = minutes === 60 ? hours + 1 : hours;
    const adjustedMinutes = minutes === 60 ? 0 : minutes;

    setTotalHours(totalHoursValue.toFixed(2));
    setFormattedHours(`${adjustedHours}h ${adjustedMinutes.toString().padStart(2, '0')}m`);
  };

  const handleTimeChange = () => {
    const startTime = form.getFieldValue('startTime');
    const endTime = form.getFieldValue('endTime');
    calculateHours(startTime, endTime);
  };

  // Handle task selection
  const handleTaskSelect = (taskName: string) => {
    const selectedTask = tasks.find(task => task.taskName === taskName);
    if (selectedTask) {
      form.setFieldsValue({
        title: selectedTask.taskName,
        description: selectedTask.taskDesc || '',
      });
    }
  };

  const onFinish = async (values: any) => {
    try {
      setLoading(true);

      if (!values.description || !values.startDate || !values.startTime || !values.endTime) {
        message.error('Please fill all required fields');
        return;
      }

      if (values.endTime.isBefore(values.startTime)) {
        message.error('End time must be after start time');
        return;
      }

      const formattedData = {
        description: values.description,
        title: values.title || values.description,
        status: "Pending",
        date: values.startDate.format('DD-MM-YYYY'),
        startTime: values.startTime.format('HH:mm'),
        endTime: values.endTime.format('HH:mm'),
        hours: totalHours,
        userId,
      };

      const response = await addOvertime(formattedData);

      if (response) {
        message.success('Overtime added successfully!');
        form.resetFields(); // Reset form fields
        onTaskCreated(); // Refresh the parent component's data
        onClose(); // Close the modal
      } else {
        message.error(response.message || 'Failed to add overtime');
      }
    } catch (error: any) {
      console.error('Submission error:', error);
      message.error(error.response?.data?.message || 'Form submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const disabledStartHours = () => {
    const endTime = form.getFieldValue('endTime');
    if (!endTime) return [];
    return Array.from({ length: 24 }, (_, i) => i).filter(hour => hour > endTime.hour());
  };

  const disabledStartMinutes = (selectedHour: number) => {
    const endTime = form.getFieldValue('endTime');
    if (!endTime || selectedHour !== endTime.hour()) return [];
    return Array.from({ length: 60 }, (_, i) => i).filter(minute => minute > endTime.minute());
  };

  const disabledEndHours = () => {
    const startTime = form.getFieldValue('startTime');
    if (!startTime) return [];
    return Array.from({ length: 24 }, (_, i) => i).filter(hour => hour < startTime.hour());
  };

  const disabledEndMinutes = (selectedHour: number) => {
    const startTime = form.getFieldValue('startTime');
    if (!startTime || selectedHour !== startTime.hour()) return [];
    return Array.from({ length: 60 }, (_, i) => i).filter(minute => minute < startTime.minute());
  };

  return (
    <Modal

      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      centered={false} // Disable centered to use top positioning
      destroyOnClose
      className="custom-modal"
      style={{
        bottom: 20,
        top: 20,
        maxHeight: 'calc(100vh - 3  0px)', // Leaves 20px space at bottom
        margin: '0 auto',
        position: 'relative',
        // paddingBottom: 20 // Adds space inside modal at bottom
      }}
      bodyStyle={{
        overflowY: "auto",
        fontFamily: "inter, sans-serif",
      }}
      styles={{
        header: {
          padding: "12px",
          position: "sticky",
          top: 0,
          backgroundColor: "#f1f5ffff",
        },
        content: {
          padding: 0,
          overflow: "hidden",
        },
      }}
      title={
        <div
          style={{
            color: "#4b5563",
            fontFamily: "inter, sans-serif",
            fontSize: "20px",
            fontWeight: 500,
          }}
        >
          Add Overtime
        </div>
      }
    >
      <Form
        onFinish={onFinish}
        form={form}
        layout="horizontal"
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 19 }}
        colon={false}
        style={{ margin: 0 }}
      >
        <div className="elements">
          <Form.Item
            name="title"
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Title <span className="text-danger">*</span></span>}
            rules={[{ required: true, message: 'Please select a task' }]}

            required={false}

          >
            <Select
              showSearch
              className='CustomSelect'
              placeholder="Select a task"
              optionFilterProp="children"
              loading={taskLoading}
              onChange={handleTaskSelect}


              // style={{ width: '70%' }}
              filterOption={(input, option) =>
                typeof option?.children === 'string' && (option.children as string).toLowerCase().includes(input.toLowerCase())
              }
            >
              {tasks.map(task => (
                <Option key={task._id} value={task.taskName}>
                  {task.taskName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Task Description<span className="text-danger">*</span></span>}
            rules={[{ required: true, message: 'Please enter a description' }]}

            required={false}

          >
            <Input.TextArea
              rows={3}
              // style={{ width: "70%" }}
              className="Allinputtextarea"
              placeholder="Description will automatically fill when title select"
            />
          </Form.Item>

          <Form.Item
            name="startDate"
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Date <span className="text-danger">*</span></span>}
            rules={[{ required: true, message: 'Please select date' }]}

            required={false}

          >
            <DatePicker format="DD-MM-YYYY" className='Allinput' />
          </Form.Item>

          <Form.Item
            name="startTime"
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Start Time <span className="text-danger">*</span></span>}
            rules={[{ required: true, message: 'Please select start time' }]}

            required={false}
          >
            <TimePicker
              use12Hours
              className='Allinput'
              format="h:mm a"
              // style={{ width: '70%' }}
              disabledHours={disabledStartHours}
              disabledMinutes={disabledStartMinutes}
              onChange={handleTimeChange}
            />
          </Form.Item>

          <Form.Item
            name="endTime"
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>End Time <span className="text-danger">*</span></span>}
            rules={[{ required: true, message: 'Please select end time' }]}

            required={false}

          >
            <TimePicker
              use12Hours
              format="h:mm a"
              className='Allinput'
              // style={{ width: '70%' }}
              disabledHours={disabledEndHours}
              disabledMinutes={disabledEndMinutes}
              onChange={handleTimeChange}
            />
          </Form.Item>

          <Form.Item
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Total Hours</span>}

            required={false}

          >
            <Input
              value={` ${formattedHours}`}
              disabled
              className='Allinput'
            // style={{ width: '70%' }}
            />
          </Form.Item>

          <Form.Item
            name="status"
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Status</span>}
            initialValue="Pending"

            required={false}

          >
            <Select placeholder="Select status" className='CustomSelect' disabled={true}>
              <Option value="Pending">Pending</Option>
              <Option value="In Progress">Approved</Option>
              <Option value="Completed">Rejected</Option>
            </Select>
          </Form.Item>

        </div>

        <Row justify="end">
          <Col
            xs={24}
            sm={24}
            md={24}
            xl={24}
            xxl={20}
            className="d-flex justify-content-end me-3"
          >
            <div className="button-wrapper">
              <button
                className="allcanclebutton"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>

              <button className="allsubmitebutton" onClick={() => form.submit()} disabled={isLoading}>
                {isLoading ? "Add..." : "Add"}
              </button>
            </div>
          </Col>
        </Row>

      </Form>

    </Modal>
  );
};

export default Addovertimemodal;